import { useParams, useNavigate } from "react-router-dom";
import { useTripContext } from "../TripContext";
import { useState, useMemo, useEffect } from "react";
import "../../../css/View/ParticipantDetails.css";
import { participantsAPI } from "../../../api/apiClient";
import useApi from "../../CustomHooks/useApi";
import Loading from "../../../Loading";
import ParticipantCorrections from "./ParticipantCorrections";
import { useToast } from "../../../Contexts/ToastContext";
import ConfirmWithPhrasePopup from "../../Common/ConfirmWithPhrasePopup";
import getParticipantStatusStyle from "../../../utils/getParticipantStatusStyle";

const ParticipantDetails = ({ participantGuid: participantGuidOverride = null, inline = false, onRegistrationClick = null }) => {
  const params = useParams();
  const participantGuid = participantGuidOverride || params.participantGuid;
  const variantguid = params.variantguid;
  const { tripData, updateRegistrationInContext, hasExplicitAccess } = useTripContext();
  useEffect(() => {
    if (!participantGuid) return;
    participantsAPI.logView(participantGuid).catch(() => {});
  }, [participantGuid]);

  const navigate = useNavigate();
  const { showToast } = useToast();

  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [editGeneral, setEditGeneral] = useState(false);
  const [editInsurance, setEditInsurance] = useState(false);
  const [insuranceExpanded, setInsuranceExpanded] = useState(false);

  const { callApi: saveGeneralInfo, isLoading: isSavingGeneral } = useApi();
  const { callApi: saveInsurance, isLoading: isSavingInsurance } = useApi();
  const { callApi: cancelParticipant, isLoading: isCancellingParticipant } = useApi();

  const participantWithReg = tripData?.registrations
    .flatMap((r) =>
      r.participants.map((p) => ({
        ...p,
        registrationCode: r.reservationCode,
        registrationGuid: r.reservationGuid,
        registrationConfirmedAt: r.confirmedAt,
        reservationStatus: r.status,
        variantGuid: r.variantGuid,
      }))
    )
    .find((p) => p.guid === participantGuid);

  const [formData, setFormData] = useState({
    email: participantWithReg?.email || "",
    phone: participantWithReg?.phone || "",
    coordinatorNotes: participantWithReg?.coordinatorNotes || "",
    prepayment: participantWithReg?.prepayment || 0,
    insurance: participantWithReg?.insurance || null,
    pronouns: participantWithReg?.pronouns || null,
    firstTime: participantWithReg?.firstTime || false
  });

  useEffect(() => {
    if (!participantWithReg) return;
    setFormData({
      email: participantWithReg.email || "",
      phone: participantWithReg.phone || "",
      coordinatorNotes: participantWithReg.coordinatorNotes || "",
      prepayment: participantWithReg.prepayment || 0,
      insurance: participantWithReg.insurance || null,
      pronouns: participantWithReg.pronouns || null,
      firstTime: participantWithReg.firstTime || false
    });
  }, [participantGuid]);

  const variant = useMemo(() => {
    const id = variantguid ?? participantWithReg?.variantGuid;
    return tripData?.variants?.find(v => v.guid === id);
  }, [tripData?.variants, variantguid, participantWithReg?.variantGuid]);

  const variantStarted = !!variant?.variantStarted;

  if (!participantWithReg) return <p>Nie znaleziono uczestnika.</p>;

  const canEdit = [1,2,3,4].includes(participantWithReg.status) && tripData.canEditTrip && hasExplicitAccess;
  const showCancelIcon = ![2, 3, 4, 5].includes(participantWithReg.status) && hasExplicitAccess;
  const actionLabel = [3].includes(participantWithReg.reservationStatus) ? "Anuluj osobę" : "Usuń osobę";
  const actionConfirmText = [3].includes(participantWithReg.reservationStatus) ? "Czy na pewno chcesz anulować tę osobę?" : "Czy na pewno chcesz usunąć tę osobę?";

  const handleCancelParticipant = () => setShowConfirmPopup(true);
  const handleConfirmedDelete = async () => {
    setShowConfirmPopup(false);
    await cancelParticipant(
      () => participantsAPI.cancelParticipant(participantWithReg.guid),
      async (response) => {
        await updateRegistrationInContext(response.registration);
        showToast(response.message || "Dane zaktualizowane poprawnie!", "success");
      }
    );
  };

  const handleInsuranceToggle = () => {
    setInsuranceExpanded((prev) => {
      const willExpand = !prev;
      if (willExpand) participantsAPI.logInsuranceView(participantGuid).catch(() => {});
      return willExpand;
    });
  };

  const handleSave = async (section) => {
    if (section === "general") {
      await saveGeneralInfo(() =>
        participantsAPI.updateGeneralInfo(participantWithReg.guid, {
          email: formData.email,
          phone: formData.phone,
          coordinatorNotes: formData.coordinatorNotes,
          prepayment: formData.prepayment,
          pronouns: formData.pronouns,
          firstTime: formData.firstTime
        }), async (response) => {
          await updateRegistrationInContext(response);
          setEditGeneral(false);
          showToast("Dane zaktualizowane poprawnie!", "success");
        });
    } else if (section === "insurance") {
      await saveInsurance(() =>
        participantsAPI.updateInsurance(participantWithReg.guid, {
          firstName: formData.insurance?.firstName,
          lastName: formData.insurance?.lastName,
          birthDate: formData.insurance?.birthDate,
          isChronicIllness: formData.insurance?.isChronicIllness,
        }), async (response) => {
          await updateRegistrationInContext(response);
          setEditInsurance(false);
          showToast("Dane zaktualizowane poprawnie!", "success");
        });
    }
  };

  const style = getParticipantStatusStyle(participantWithReg.status);
  const renderInput = (name, type = "text") => {
    if (type === "checkbox") return <input type="checkbox" checked={!!formData[name]} onChange={(e) => setFormData({ ...formData, [name]: e.target.checked })} />;
    return <input className="inline-input" type={type} value={formData[name] || ""} onChange={(e) => setFormData({ ...formData, [name]: e.target.value })} />;
  };
  const renderInsuranceInput = (field, type = "text") => {
    let value = formData.insurance?.[field] || "";
    if (type === "date" && value) value = value.substring(0, 10);
    return <input className="inline-input" type={type} value={value} onChange={(e) => setFormData({ ...formData, insurance: { ...formData.insurance, [field]: e.target.value } })} />;
  };
  const formatDateTime = (date) => new Date(date).toLocaleString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

  const handleRegistrationLink = () => {
    if (inline && onRegistrationClick) return onRegistrationClick(participantWithReg.registrationGuid);
    navigate(`/trips/${tripData.guid}/variants/${variantguid || participantWithReg.variantGuid}/registrations/${participantWithReg.registrationGuid}`);
  };

  return (
    <div className={`participant-container${inline ? " participant-container--inline" : ""}`}>
      {showCancelIcon && (
        <div className="trip-details-buttons">
          <div className={`trip-details-button-container ${participantWithReg.isPrimary || variantStarted ? "disabled-button" : ""}`} onClick={() => {if (participantWithReg.isPrimary || variantStarted) return; handleCancelParticipant();}} title={participantWithReg.isPrimary ? "Nie można usunąć osoby głównej" : variantStarted ? "Nie można anulować/usuwać osób po rozpoczęciu wariantu" : ""}>
            <div className="trip-details-button"><i className={`fa-solid ${isCancellingParticipant ? "fa-spinner fa-spin" : "fa-user-slash"} trip-details-button-icon`} /></div>
            <span className="trip-details-button-label">{actionLabel}</span>
          </div>
        </div>
      )}

      <div className="card">
        <div className="participant-heading">
          <i className={`fa-solid ${style.icon} participant-icon ${style.statusClass}`} />
          <span className={`participant-reg-name ${style.strikeThrough ? "participantstrike-through" : ""}`}>
            {participantWithReg.preferredName}
            {canEdit && <div onClick={() => (editGeneral ? handleSave("general") : setEditGeneral(true))} className="edit-icon"><i className={`fa-solid ${isSavingGeneral ? "fa-spinner fa-spin" : editGeneral ? "fa-floppy-disk" : "fa-pen-to-square"}`} /></div>}
          </span>
        </div>
        <ul className="detail-list">
          {isSavingGeneral ? <Loading text="Zapisywanie..."/> : <>
            <li><strong>Rezerwacja:</strong> <span className="participant-link" onClick={handleRegistrationLink}>{participantWithReg.registrationCode}</span></li>
            <li><strong>Status osoby:</strong> <span className={style.statusClass}><strong>{style.label}</strong></span></li>
            {participantWithReg.status === 4 && participantWithReg.cancelledAt && <li><strong>Anulowanx:</strong> {formatDateTime(participantWithReg.cancelledAt)}</li>}
            <li><strong>Email:</strong> {editGeneral ? renderInput("email") : participantWithReg.email}</li>
            <li><strong>Telefon:</strong> {editGeneral ? renderInput("phone") : participantWithReg.phone}</li>
            <li><strong>Osoba główna:</strong> {participantWithReg.isPrimary ? "Tak" : "Nie"}</li>
            <li><strong>Zaliczka:</strong> {editGeneral ? renderInput("prepayment", "number") : `${participantWithReg.prepayment ? participantWithReg.prepayment.toFixed(2) + " zł" : "- nie dotyczy -"}`}</li>
            <li><strong>Zaimki:</strong> {editGeneral ? renderInput("pronouns") : participantWithReg.pronouns}</li>
            <li><strong>Pierwszy raz:</strong> {editGeneral ? renderInput("firstTime", "checkbox") : participantWithReg.firstTime ? "Tak" : "Nie"}</li>
            <li><strong>Notatka koord.:</strong> {editGeneral ? renderInput("coordinatorNotes") : participantWithReg.coordinatorNotes}</li>
          </>}
        </ul>
      </div>

      <div className="card">
        <div className="rd-section-heading">Opłaty</div>
        <ul className="detail-list">
          <li><strong>Koszty adm.:</strong> {`${participantWithReg.administrativeCosts?.toFixed(2)} zł`}</li>
          <li><strong>Elementy:</strong> {`${participantWithReg.elementsPrice?.toFixed(2)} zł`}</li>
          <li><strong>KR:</strong> {participantWithReg.krCost !== null ? `${participantWithReg.krCost?.toFixed(2)} zł` : "– nie dotyczy –"}</li>
          <li><strong>NNW:</strong> {participantWithReg.nnwCost !== null ? `${participantWithReg.nnwCost?.toFixed(2)} zł` : "– nie dotyczy –"}</li>
          <li><strong>TFG:</strong> {participantWithReg.tfgCost !== null ? `${participantWithReg.tfgCost?.toFixed(2)} zł` : "– nie dotyczy –"}</li>
          <ParticipantCorrections corrections={participantWithReg.corrections} participantGuid={participantGuid} canBeEdited={canEdit}/>
          <li className="total-row"><strong>Łącznie:</strong> <span className="total-amount">{participantWithReg.totalCost?.toFixed(2)} zł</span></li>
        </ul>
      </div>

      {participantWithReg.insurance && canEdit && (
        <div className="card">
          <div className="rd-section-heading insurance-heading" onClick={handleInsuranceToggle}>Ubezpieczenie
            {insuranceExpanded && canEdit && <div onClick={(e) => { e.stopPropagation(); editInsurance ? handleSave("insurance") : setEditInsurance(true); }} className="edit-icon"><i className={`fa-solid ${isSavingInsurance ? "fa-spinner fa-spin" : editInsurance ? "fa-floppy-disk" : "fa-pen-to-square"}`} /></div>}
            <i className={`fa-solid ${insuranceExpanded ? "fa-chevron-up" : "fa-chevron-down"}`} />
          </div>
          {insuranceExpanded && <ul className="detail-list">
            {isSavingInsurance ? <Loading text="Zapisywanie..."/> : <>
              <li><strong>Imię:</strong> {editInsurance ? renderInsuranceInput("firstName") : participantWithReg.insurance.firstName}</li>
              <li><strong>Nazwisko:</strong> {editInsurance ? renderInsuranceInput("lastName") : participantWithReg.insurance.lastName}</li>
              <li><strong>Data urodzenia:</strong> {editInsurance ? renderInsuranceInput("birthDate", "date") : participantWithReg.insurance.birthDate?.substring(0, 10)}</li>
              <li><strong>Choroba przewlekła:</strong> {editInsurance ? <input type="checkbox" checked={formData.insurance?.isChronicIllness || false} onChange={(e) => setFormData({ ...formData, insurance: { ...formData.insurance, isChronicIllness: e.target.checked } })} /> : participantWithReg.insurance.isChronicIllness ? "Tak" : "Nie"}</li>
            </>}
          </ul>}
        </div>
      )}

      {showConfirmPopup && <ConfirmWithPhrasePopup text={actionConfirmText} phrase="okoń" onConfirm={handleConfirmedDelete} onCancel={() => setShowConfirmPopup(false)} />}
    </div>
  );
};

export default ParticipantDetails;
